import { FormattedMessage } from 'react-intl';
import { useState } from 'react';
import { useFilterSet } from 'hooks/useFilterSet';
import { useFormContext } from 'react-hook-form';
import { Menu, MenuItem } from '@mui/material';
import { createFilterSetMatcher } from 'utils/filterSetMatcher';
import { DialogStep, DIALOG_STEPS } from '../modals/constants';
import { AdvancedFilterData } from '../FilterFormController';
import { FilterChip } from './FilterChip';
import { TbIcon } from 'components/Figma';

interface FilterSetsProps {
  setDialog?: (dialog: DialogStep) => void;
  setFilterSetToDelete?: (id: string | null) => void;
  setFilterSetToRename?: (id: string | null) => void;
  setFilterSetToMerge?: (id: string | null) => void;
}

function FilterSets({
  setDialog = () => {},
  setFilterSetToDelete = () => {},
  setFilterSetToRename = () => {},
  setFilterSetToMerge = () => {},
}: FilterSetsProps) {
  // Use the extracted Filter matching logic
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null);

  const { reset, setValue } = useFormContext<AdvancedFilterData>();
  const { filterSets } = useFilterSet();
  const formContext = useFormContext<AdvancedFilterData>();

  const handleApplyFilter = (id: string) => {
    const currentValues = formContext.getValues();
    const matcher = createFilterSetMatcher(filterSets, currentValues.formSelectedFilterSet ?? null);

    setValue('formLastClickedFilterSet', id);

    try {
      const strategy = matcher.determineApplicationStrategy(id, currentValues);

      switch (strategy.action) {
        case 'deactivate':
          reset({
            ...strategy.newValues,
            formSelectedFilterSet: null,
            formLastClickedFilterSet: currentValues.formLastClickedFilterSet,
          });
          break;

        case 'switch':
          formContext.reset({
            ...strategy.newValues,
            formSelectedFilterSet: id,
            formLastClickedFilterSet: currentValues.formLastClickedFilterSet,
          });
          break;

        case 'apply':
          formContext.reset({
            ...strategy.newValues,
            formSelectedFilterSet: id,
            // Clear formLastClicked since we now have an active filter
            formLastClickedFilterSet: null,
          });
          break;

        case 'prompt':
          // Open merge dialog when user confirmation is needed
          setFilterSetToMerge(id);
          setDialog(DIALOG_STEPS.FILTER_MERGE);
          break;

        default:
          console.warn(`Unknown application strategy: ${strategy.action}`);
      }
    } catch (error) {
      console.error('Error applying filter set:', error);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, filterId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedFilterId(filterId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFilterId(null);
  };

  const handleDeleteFilter = () => {
    if (selectedFilterId && setDialog && setFilterSetToDelete) {
      setFilterSetToDelete(selectedFilterId);
      setDialog('filterDelete');
    }
    handleMenuClose();
  };

  const handleRenameFilter = () => {
    if (selectedFilterId && setDialog && setFilterSetToRename) {
      setFilterSetToRename(selectedFilterId);
      setDialog('filterRename');
    }
    handleMenuClose();
  };

  if (filterSets.length === 0) {
    return (
      <div>
        <p className="text-md font-medium border-b pb-2">
          <FormattedMessage id="advanced-filter-set-saved" defaultMessage="Previously saved filter sets" />
          <span className="text-sm text-gray-500 ml-2">(0/10)</span>
        </p>
        <p className="text-sm text-gray-500 pt-4">
          <FormattedMessage
            id="advanced-filter-set-empty"
            defaultMessage="No saved filter sets found. Save your current filters to see them here."
          />
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-md font-medium border-b pb-2">
        <FormattedMessage id="advanced-filter-set-saved" defaultMessage="Previously saved filter sets" />
        <span className="text-sm text-gray-500 ml-2">
          ({filterSets.length}
          {'/10 '}
          <FormattedMessage id="general.label.saved" defaultMessage="saved" />)
        </span>
      </p>
      <div className="flex pt-4 gap-1.5 flex-wrap">
        {filterSets.map(filterSet => {
          // Use the new utility to get display state
          const currentValues = formContext.getValues();
          const matcher = createFilterSetMatcher(filterSets, currentValues.formSelectedFilterSet ?? null);
          const displayState = matcher.getFilterSetDisplayState(filterSet.filterSetId, currentValues);

          const isLastClicked =
            currentValues.formLastClickedFilterSet === filterSet.filterSetId &&
            !displayState.isActive &&
            !displayState.isSelected;

          return (
            <div key={filterSet.filterSetId} className="relative">
              <FilterChip
                icon={displayState.isActive ? <TbIcon name="Checkmark" /> : undefined}
                deleteIcon={<TbIcon name="More" size="xs" />}
                onDelete={(event: React.MouseEvent<Element, MouseEvent>) =>
                  handleMenuOpen(event as React.MouseEvent<HTMLElement>, filterSet.filterSetId)
                }
                name={filterSet.name}
                label={filterSet.name}
                active={displayState.isActive}
                selected={displayState.isSelected}
                lastClicked={isLastClicked}
                onClick={() => handleApplyFilter(filterSet.filterSetId)}
                title={displayState.tooltip}
                className="gap-3"
              />
            </div>
          );
        })}
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleRenameFilter}>
          <FormattedMessage id="advanced-filter-rename" defaultMessage="Rename" />
        </MenuItem>
        <MenuItem onClick={handleDeleteFilter}>
          <FormattedMessage id="advanced-filter-delete" defaultMessage="Delete" />
        </MenuItem>
      </Menu>
    </div>
  );
}

export { FilterSets };
